/**
 * Classifies a Sentry event by ORIGIN — Panda's own shell code vs the GENERATED
 * app running inside the preview sandbox — so the two never get filed as the
 * same crash.
 *
 * Every live preview is a cross-origin sandbox iframe: E2B (`*.e2b.app` /
 * `*.e2b.dev`) today, legacy WebContainer (`*.webcontainer-api.io`) before it.
 * The generated app is user-authored (largely LLM-generated) code, and a React
 * runtime crash inside it — most notably "Rendered more hooks than during the
 * previous render", which is a signature LLM hooks-order mistake — is a bug in
 * THAT app, not in the Panda workspace shell that hosts it.
 *
 * The Panda shell render tree is provably free of hooks-order violations
 * (`react-hooks/rules-of-hooks` passes across it), so such an error surfacing on
 * `/project/:id` is a generated-app error leaking through the preview, not ours.
 * Left untagged it groups with genuine shell crashes and makes a user's buggy
 * app look like a platform outage (Sentry JAVASCRIPT-NEXTJS-1H). Tagging by
 * origin — and fingerprinting preview errors apart — keeps the shell issue
 * stream honest and lets generated-app crashes be triaged on their own.
 *
 * Pure and dependency-free (no Sentry import) so it is trivially unit-testable
 * and reusable on any capture path. It accepts the minimal Sentry event shape it
 * inspects; a real `@sentry/nextjs` `ErrorEvent` satisfies it structurally.
 * Mirrors the shape of `in-app-browser-noise.ts`.
 */

export const GENERATED_APP_PREVIEW_ORIGIN = "generated-app-preview" as const;
export const PANDA_SHELL_ORIGIN = "panda-shell" as const;

export type ErrorOrigin =
  | typeof GENERATED_APP_PREVIEW_ORIGIN
  | typeof PANDA_SHELL_ORIGIN;

interface OriginStackFrame {
  filename?: string;
  abs_path?: string;
  module?: string;
}

interface OriginExceptionValue {
  stacktrace?: { frames?: OriginStackFrame[] };
}

export interface ErrorOriginEvent {
  exception?: { values?: OriginExceptionValue[] };
  /** Sentry's `Event.tags` — read to honour an origin the SDK/bridge already set. */
  tags?: Record<string, unknown> | undefined;
}

// Cross-origin preview-sandbox hosts. A frame served from one of these can only
// be the generated app (or its injected bridge) — the Panda shell is always on
// its own origin (panda.new / *.vercel.app), never a sandbox host. Anchored to a
// leading `.` / `//` so a lookalike path segment can't spoof it.
const SANDBOX_HOST_RE =
  /(?:\/\/|\.)[a-z0-9-]+\.(?:e2b\.(?:app|dev)|webcontainer-api\.io)\b/i;

// The error bridge Panda injects INTO the generated app to relay its runtime
// errors out of the iframe (lib/sandbox/preview-error-bridge.ts). A frame naming
// it is unambiguously preview-side, even if the sandbox host was stripped.
const INJECTED_BRIDGE_RE = /__panda-error-bridge/;

// Panda's own compiled client code. Next.js serves every app/component chunk
// from `/_next/static/…` (and server chunks from `/_next/server/…`), so a frame
// naming one is code WE shipped — as opposed to a third-party script, a browser
// extension, or an opaque/absent frame.
const PANDA_BUNDLE_RE = /\/_next\/(?:static|server)\//;

/**
 * True when a stack-frame source belongs to the Panda SHELL bundle — i.e. code
 * we shipped, running on our own origin.
 *
 * The `/_next/static/` test alone is not sufficient: the generated app inside
 * the preview sandbox is itself a Next.js app and serves the same path from
 * `*.e2b.app`. A sandbox (or injected-bridge) frame is therefore excluded even
 * when it matches the bundle path.
 *
 * Exported so other noise classifiers can ask "does Panda own this stack?"
 * without duplicating the sandbox-host regex — see `cross-origin-frame-noise.ts`,
 * which only drops an event when NO frame is Panda-owned.
 */
export function isPandaShellFrameSource(source: string | undefined | null): boolean {
  if (!source) return false;
  if (SANDBOX_HOST_RE.test(source) || INJECTED_BRIDGE_RE.test(source)) return false;
  return PANDA_BUNDLE_RE.test(source);
}

/** The source string Sentry gives us for a frame, in preference order. */
function frameSourceOf(frame: OriginStackFrame): string {
  return frame.filename ?? frame.abs_path ?? frame.module ?? "";
}

function frameIsPreview(frame: OriginStackFrame): boolean {
  const source = frameSourceOf(frame);
  return SANDBOX_HOST_RE.test(source) || INJECTED_BRIDGE_RE.test(source);
}

/**
 * True when the event originates in the generated app inside the preview
 * sandbox (or its injected bridge), rather than in Panda shell code.
 */
export function isGeneratedAppPreviewError(
  event: ErrorOriginEvent | null | undefined,
): boolean {
  if (!event) return false;

  // Honour an origin an upstream layer (e.g. the preview-frame relay) already
  // stamped, so a hand-forwarded error is classified even without shell frames.
  if (event.tags?.error_origin === GENERATED_APP_PREVIEW_ORIGIN) return true;

  for (const value of event.exception?.values ?? []) {
    for (const frame of value.stacktrace?.frames ?? []) {
      if (frameIsPreview(frame)) return true;
    }
  }
  return false;
}

/**
 * Classify an event's origin. Defaults to `panda-shell`: an event is only
 * treated as a generated-app crash when we have a positive preview signal, so a
 * genuine shell error is never mislabelled and hidden.
 */
export function classifyErrorOrigin(
  event: ErrorOriginEvent | null | undefined,
): ErrorOrigin {
  return isGeneratedAppPreviewError(event)
    ? GENERATED_APP_PREVIEW_ORIGIN
    : PANDA_SHELL_ORIGIN;
}
